import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import type Transition from '@ember/routing/transition';
import { getOwnConfig, macroCondition } from '@embroider/macros';
import config from 'uplisting-frontend/config/environment';
import { routerHelper } from 'uplisting-frontend/utils/router';
import { getScreenSection } from 'uplisting-frontend/utils/analytics';
import BaseRoute from 'uplisting-frontend/pods/base/route';
import { formats } from 'uplisting-frontend/ember-intl';

export default class ApplicationRoute extends BaseRoute {
  @service intl!: Services['intl'];
  @service locale!: Services['locale'];
  @service sentry!: Services['sentry'];
  @service segment!: Services['segment'];
  @service analytics!: Services['analytics'];
  @service crisp!: Services['crisp'];
  @service userflow!: Services['userflow'];
  @service subscription!: Services['subscription'];
  @service newVersionNotifier!: Services['new-version-notifier'];
  @service featureFlags!: Services['feature-flags'];
  @service maintenance!: Services['maintenance'];

  @service('repositories/account-configuration')
  accountConfigurationRepository!: Services['repositories/account-configuration'];

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    this.#setSentryTransaction(transition);

    this.router.on('routeWillChange', (transition) =>
      this.#setSentryTransaction(transition),
    );

    this.router.on('routeDidChange', (transition) =>
      this.#setSentryTransaction(transition),
    );

    await this.#setupIntl();

    // Ahead of `session.setup` and every boot fetch: in maintenance we render
    // without touching an API that is very likely the reason we are down.
    const isDown = await this.maintenance.check();

    this.maintenance.poll.perform();

    if (isDown) {
      return;
    }

    await this.session.setup();
  }

  async afterModel(
    model: ApplicationRouteModel,
    transition: Transition,
  ): Promise<void> {
    super.afterModel(model, transition);

    if (this.maintenance.isDown) {
      window.document.getElementById('initial-loader')?.remove();

      return;
    }

    // /vrt renders with no backend: skip the authenticated boot fetches even
    // when a stale session is present. Stripped from production (dev/test only).
    let isVrtRoute = false;

    if (macroCondition(getOwnConfig<{ enableVrt: boolean }>().enableVrt)) {
      isVrtRoute = transition.to?.name === 'vrt';
    }

    if (!isVrtRoute && this.session.isAuthenticated) {
      await Promise.all([
        this.session.fetchCurrentUser(),
        this.accountConfigurationRepository.findRecord('me'),
      ]);

      await this.featureFlags.initialize();

      if (this.session.currentUser) {
        this.crisp.configure();
        this.userflow.configure();
      }

      this.subscription.checkPaused();
      this.newVersionNotifier.listenForNewRelease.perform();
    }

    window.document.getElementById('initial-loader')?.remove();
  }

  async #setupIntl(): Promise<void> {
    this.intl.setFormats(formats);

    this.intl.setOnMissingTranslation((key: string, locales: string[]) => {
      const message = `Missing translation for key: "${key}" for locales: "${locales}"`;

      if (config.IS_PROD) {
        this.sentry.captureMessage(message);
      } else {
        throw new Error(message);
      }

      return '';
    });

    // Resolves the stored locale (or en-gb), fetches that locale's translations,
    // sets the active locale, and refreshes the locale-dependent caches +
    // validation messages. Guarded so a failed translations fetch degrades to a
    // booting app shell (Sentry-logged) rather than stranding the loader.
    try {
      await this.locale.setLocale(this.locale.resolve());
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  // https://github.com/josemarluedke/ember-cli-segment?tab=readme-ov-file#identifying-the-user
  public identifyUser() {
    this.analytics.identifyUser();

    if (this.session.currentUser) {
      this.crisp.identifyUser(this.session.currentUser);
      this.userflow.identify(this.session.currentUser);
    }
  }

  // Called by ember-cli-segment on every route change (its instance-initializer
  // invokes `trackPageView` on the application route when defined). We keep the
  // generic Segment page view and additionally emit a named screen-view event
  // enriched with the route and product section (UPL-8231).
  public trackPageView() {
    this.segment.trackPageView();

    const routeName = this.router.currentRouteName;

    // Skip the intermediate loading/error substates Ember transitions through
    // so the named event reflects real screens, not transition noise.
    if (!routeName || this.#isTransientRoute(routeName)) {
      return;
    }

    this.analytics.trackScreenView({
      screen_name: routeName,
      route: this.router.currentURL,
      section: getScreenSection(routeName),
    });
  }

  #isTransientRoute(routeName: string): boolean {
    return /(^|[._-])(loading|error)$/.test(routeName);
  }

  #setSentryTransaction(transition: Transition): void {
    const name = transition.to?.name;

    if (name) {
      this.sentry.getCurrentScope().setTransactionName(name);
    }
  }

  @action
  loading(transition: Transition): true {
    routerHelper.targetRoute = transition.to?.name;

    return true; // allows the loading template to be shown
  }
}

type ApplicationRouteModel = RouteModel<ApplicationRoute>;
